/**
 * AidesCrossLink — grille des 5 aides cumulables (cross-link interne).
 *
 * Pattern : cards compactes 4-5 colonnes responsive, chacune liant vers
 * /particuliers/aides/[slug]. Icône halo gradient brand, montant en gradient
 * accent → brand. Hover lift.
 *
 * Utilisée à la fin des pages parcours, travaux, hub aides, guides aides.
 */

import { ArrowRight } from 'lucide-react';
import { Container } from '../../layout/Container';
import { StaggerGrid, StaggerItem } from '../../motion/StaggerGrid';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import {
  AIDES_PARTICULIERS,
  type AideStub,
} from '../../../config/aides-particuliers';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
} from '../../../config/audience/particulier.ui';
import { ParticulierSectionHeader } from './SectionHeader';

export interface ParticulierAidesCrossLinkProps {
  /** Eyebrow (par défaut : "Aides mobilisables") */
  eyebrow?: string;
  /** Titre (par défaut : "Les 5 aides cumulables pour votre projet") */
  title?: string;
  /** Description optionnelle */
  description?: string;
  /** Filtre éventuel : afficher seulement certaines aides par slug */
  filterSlugs?: string[];
  /** Variante "compacte" : 5 colonnes lg / 4 colonnes lg selon nombre d'items */
  variant?: 'compact' | 'rich';
  /** Lien "Voir toutes les aides" affiché à droite du header */
  showAllLink?: boolean;
  /** Surcharger les classes de section */
  sectionClass?: string;
}

export function ParticulierAidesCrossLink({
  eyebrow = 'Aides mobilisables',
  title = 'Les 5 aides cumulables pour votre projet',
  description = "MaPrimeRénov’, Prime énergie (CEE), éco-PTZ, TVA à 5,5 % et Coup de pouce peuvent se cumuler sur un même chantier.",
  filterSlugs,
  variant = 'compact',
  showAllLink = true,
  sectionClass,
}: ParticulierAidesCrossLinkProps) {
  const aides: AideStub[] = filterSlugs
    ? AIDES_PARTICULIERS.filter((a) => filterSlugs.includes(a.slug))
    : AIDES_PARTICULIERS;

  if (aides.length === 0) return null;

  const colsClass =
    variant === 'compact'
      ? 'sm:grid-cols-2 lg:grid-cols-5'
      : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className={cn(PARTICULIER_SECTION.standard, sectionClass)}>
      <Container>
        <ParticulierSectionHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          rightSlot={
            showAllLink ? (
              <a
                href="/particuliers/aides"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors hover:text-brand-800"
              >
                Voir toutes les aides
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </a>
            ) : undefined
          }
        />

        <StaggerGrid className={cn('mt-10 grid gap-3', colsClass)}>
          {aides.map((aide) => {
            const Icon = getIcon(aide.iconName);
            return (
              <StaggerItem key={aide.slug}>
                <a
                  href={`/particuliers/aides/${aide.slug}`}
                  className="group flex h-full flex-col gap-2 rounded-xl border border-stone-200 bg-paper p-4 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,10,.08)]"
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-150 ease-out group-hover:scale-105">
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <p className="mt-1 font-display text-sm font-bold text-ink">{aide.title}</p>
                  <p
                    className={cn(
                      'text-xs font-bold tabular-nums',
                      PARTICULIER_GRADIENT.aideAmount,
                    )}
                  >
                    {aide.amount}
                  </p>
                  <p className="mt-auto inline-flex items-center gap-1 pt-2 text-[11px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                    En savoir plus
                    <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                  </p>
                </a>
              </StaggerItem>
            );
          })}
        </StaggerGrid>
      </Container>
    </section>
  );
}
